import { useEffect } from 'react';

/**
 * A set of helper functions and hooks to accessibly hide outside DOM elements.
 *
 * The `aria-modal` property introduced by ARIA 1.1 replaces aria-hidden for informing assistive
 * technologies that content outside a dialog is inert. However, in legacy dialog implementations
 * where `aria-hidden` is used to make content outside a dialog inert for assistive technology
 * users, it is important that:
 * 1. `aria-hidden` is set to `true` on each element containing a portion of the inert layer.
 * 2. The dialog element is not a descendant of any element that has `aria-hidden` set to `true`.
 *
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices-1.2/#dialog_modal
 * @see Inspiration https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/overlays/src/ariaHideOutside.ts
 */

/**
 * A map of all hidden elements with a count referring to the number of times it has been hidden.
 * Allows for `accessiblyHideOutsideElements` or `useInertA11yBackground` to be called multiple
 * times. `aria-hidden` is only removed once the count reaches zero.
 */
const elementCountMap = new WeakMap();

/**
 * Accessibility hides all elements in the DOM outside the given target elements using aria-hidden.
 *
 * @param targets Elements that should remain accessibily visible.
 * @param root Element where DOM traversal will begin.
 * @returns A function that will restore all hidden elements.
 */
function accessiblyHideOutsideElements(targets) {
  let root = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document.body;
  const visibleNodes = new Set(targets);
  const hiddenNodes = new Set();

  /**
   * Creates a `TreeWalker` object so we don't need to traverse the DOM ourselves.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/TreeWalker
   */
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      // Skip this node and its children if it is one of the target nodes.
      // Also skip children of already hidden nodes, as `aria-hidden` is recursive.
      if (visibleNodes.has(node) || hiddenNodes.has(node.parentElement)) {
        return NodeFilter.FILTER_REJECT;
      }

      // Skip this node but continue to children if one of the targets is inside the node.
      if (targets.some(target => node.contains(target))) {
        return NodeFilter.FILTER_SKIP;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  const hide = node => {
    const elementCount = elementCountMap.get(node) ?? 0;

    // If `aria-hidden` attribute existed on element initially, don't do anything.
    if (node.getAttribute("aria-hidden") === "true" && elementCount === 0) {
      return;
    }

    // If `aria-hidden` attribute did not exist on element initially and it hasn't
    // already been hidden, hide it.
    if (elementCount === 0) {
      node.setAttribute("aria-hidden", "true");
    }
    hiddenNodes.add(node);
    elementCountMap.set(node, elementCount + 1);
  };

  // Start at the first child node of the root since the root should never have
  // `aria-hidden` applied to it.
  let currentNode = walker.nextNode();

  // Iterate over all nodes.
  while (currentNode) {
    hide(currentNode);
    currentNode = walker.nextNode();
  }

  // Return a function that will restore hidden elements.
  return () => {
    for (const node of hiddenNodes) {
      const elementCount = elementCountMap.get(node) ?? 0;
      if (elementCount === 1) {
        node.removeAttribute("aria-hidden");
        elementCountMap.delete(node);
      } else {
        // Don't restore element if it has been hidden by another call to
        // `accessiblyHideOutsideElements` or `useInertA11yBackground`.
        elementCountMap.set(node, elementCount - 1);
      }
    }
  };
}

/**
 * Accessibility hides all elements in the DOM outside the given target refs using aria-hidden.
 *
 * @param shouldHide Whether outside DOM elements should be accessibly hidden.
 * @param targets Refs representing elements that should remain accessibily visible.
 * @param root The element where DOM traversal begins.
 * @returns A function that will restore all hidden elements.
 */
function useInertA11yBackground(shouldHide, targetRefs) {
  let root = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : document.body;
  useEffect(() => {
    // Convert refs to elements
    const targetElements = targetRefs.reduce((elements, targetRef) => {
      return targetRef.current ? [...elements, targetRef.current] : elements;
    }, []);
    if (!targetElements.length) {
      return undefined;
    }
    let restoreElements = null;

    // Hide elements
    if (shouldHide) {
      restoreElements = accessiblyHideOutsideElements(targetElements, root);
    }
    return () => {
      // Restore previously hidden elements
      if (shouldHide) {
        restoreElements?.();
      }
    };
  }, [shouldHide, targetRefs, root]);
}

export { accessiblyHideOutsideElements, useInertA11yBackground };
